import React, { memo, useCallback } from 'react';
import type { AddressType } from '@sweetgreen/garnish';
import { LoadingPlaceholder, theme, useResponsive } from '@sweetgreen/garnish';
import { BREAKPOINTS, useFluidSize, VStack } from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';
import type { OrderStatus } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useOpenBag } from '../../../../components/Bag/Bag.hooks';
import { useOrderStatusNavigation } from '../../../OrderStatusScreen/OrderStatus.navigation';
import { useOrderStatusTelemetry } from '../../../OrderStatusScreen/OrderStatus.telemetry';
import type { GetOrderStatusQuery } from '../../GraphQL/OrderStatus.query.generated';
import { useHomeLocalization } from '../../hooks';
import { HomeScreenCTA } from '../HomeScreenCTA';
import { HomeScreenGreetings } from '../HomeScreenGreetings';

export const HomeScreenWelcomePane = memo(
  (props: HomeScreenWelcomePaneProps) => {
    const {
      data,
      isLoading,
      isLoadingStatus,
      cartOrderType,
      hasCart,
      seasonalHeroHeadlineText,
      orderPendingFeedbackId,
    } = props;

    // ─── Context ──────────────────────────────────────────────────

    const { t } = useLocalizationContext();
    const { currentBreakpoint, match } = useResponsive();
    const fluid = useFluidSize({
      min: BREAKPOINTS[1].MIN,
      max: BREAKPOINTS[3].MIN,
    });
    const contentSpacing = fluid(24, 40);

    // ─── Localization ─────────────────────────────────────────────

    const {
      shouldShowCartAction,
      shouldShowStatusAction,
      greetingMode,
      orderId,
      orderType,
      enhancedOrderStatus,
      restaurantName,
      dropoffLocation,
      deliveryAddressName,
      timeEstimate,
      dayOfWeek,
      wantedTimeTs,
      timeRangeStartTs,
      timeRangeEndTs,
    } = useHomeLocalization({
      isLoading: isLoading || isLoadingStatus,
      hasCart,
      orderStatus: data?.orderStatus as OrderStatus,
      orderPendingFeedbackId,
    });

    // ─── Params ───────────────────────────────────────────────────

    const customerName = useCustomer().customer?.firstName;
    const greeting = t('home.welcome.greeting', { greetingMode, customerName });

    const heroHeadline = !shouldShowStatusAction
      ? seasonalHeroHeadlineText
      : t('home.welcome.heroHeadline', { orderType, enhancedOrderStatus });

    const hasCartOnMobile = shouldShowCartAction && currentBreakpoint.isMobile;
    const shouldShowCallToAction =
      !hasCartOnMobile &&
      (isLoadingStatus || shouldShowCartAction || shouldShowStatusAction);

    const isPendingFeedbackStatus = enhancedOrderStatus === 'feedback';

    // ─── Callbacks ────────────────────────────────────────────────

    const openBag = useOpenBag({ shouldUpdateCart: true });
    const { handleOnRateOrder, handleOrderStatus } = useOrderStatusNavigation();
    const { handleOrderStatusEvent } = useOrderStatusTelemetry();

    const handleOnPress = useCallback(() => {
      if (shouldShowCartAction) {
        openBag();

        return;
      }

      if (isPendingFeedbackStatus && orderId) {
        handleOnRateOrder(orderId);

        return;
      }

      if (orderId) {
        handleOrderStatus(orderId);
        handleOrderStatusEvent('home.cta');
      }
    }, [
      isPendingFeedbackStatus,
      shouldShowCartAction,
      orderId,
      handleOnRateOrder,
      handleOrderStatus,
      handleOrderStatusEvent,
      openBag,
    ]);

    return (
      <VStack customSpacing={contentSpacing} {...props}>
        <HomeScreenGreetings
          testID={customerName ? 'home.welcome.greetings-logged-in' : undefined}
          greeting={greeting}
          heroHeadline={heroHeadline}
        />
        {shouldShowCallToAction && isLoadingStatus ? (
          <LoadingPlaceholder
            palette="darkGreen"
            rowHeight={match([72, 100])}
            rows={1}
            columns={1}
            borderRadius={theme.radius.large}
          />
        ) : null}
        {shouldShowCallToAction && !isLoadingStatus ? (
          <HomeScreenCTA
            isPendingFeedbackStatus={isPendingFeedbackStatus}
            shouldShowCartAction={shouldShowCartAction}
            shouldShowStatusAction={shouldShowStatusAction}
            cartOrderType={cartOrderType}
            orderType={orderType}
            enhancedOrderStatus={enhancedOrderStatus}
            restaurantName={restaurantName}
            dropoffLocation={dropoffLocation}
            deliveryAddressName={deliveryAddressName}
            timeEstimate={timeEstimate}
            dayOfWeek={dayOfWeek}
            wantedTimeTs={wantedTimeTs}
            timeRangeStartTs={timeRangeStartTs}
            timeRangeEndTs={timeRangeEndTs}
            onPress={handleOnPress}
          />
        ) : null}
      </VStack>
    );
  },
);

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type HomeScreenWelcomePaneProps = {
  readonly data?: GetOrderStatusQuery;
  readonly isLoading: boolean;
  readonly isLoadingStatus: boolean;
  readonly hasCart: boolean;
  readonly cartOrderType: AddressType;
  readonly seasonalHeroHeadlineText: string;
  readonly orderPendingFeedbackId?: string;
};
